/**
 * 文案生成工作流容器
 * 管理分步视图：Step 1 输入参数 → Step 2 结果编辑
 */

import { useState, useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import type { Project, GenerationMeta, CopywritingFormCache } from '@/types'
import type { ReceiptType } from '@/config/receipts'
import { useCopywriting } from '@/hooks/useCopywriting'
import { useAssets } from '@/hooks/useAssets'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { Button } from '@/components/ui/button'
import { CopywritingInputForm } from './CopywritingInputForm'
import { CopywritingResults } from './CopywritingResults'

interface CopywritingWorkflowProps {
  project: Project
  onNavigateToNotes?: () => void  // 前往笔记库回调
}

type WorkflowStep = 'input' | 'results'

export function CopywritingWorkflow({ project, onNavigateToNotes }: CopywritingWorkflowProps) {
  const [step, setStep] = useState<WorkflowStep>('input')
  const [lastFormData, setLastFormData] = useState<CopywritingFormCache | null>(null)

  const { assets } = useAssets(project.id)
  const { copywritingStyles, copywritingStyleById } = useNoteStyles()
  const {
    isGenerating,
    isAnalyzing,
    error,
    generatedNotes,
    referenceAnalysis,
    generationProgress,
    loadGeneratedCache,
    analyzeReference,
    generateCopywriting,
    updateGeneratedNote,
    toggleNoteSelection,
    toggleSelectAll,
    saveSelectedNotes,
    clearResults,
    loadFormCache,
    saveFormCache,
  } = useCopywriting(project.id)

  // 组件加载时，如果有生成结果则显示结果页
  useEffect(() => {
    loadGeneratedCache()
  }, [loadGeneratedCache])

  useEffect(() => {
    if (generatedNotes.length > 0 || isGenerating) {
      setStep('results')
    }
  }, [generatedNotes.length, isGenerating])
  
  // 从缓存中恢复 lastFormData（用于页面切换后返回）
  useEffect(() => {
    if (!lastFormData) {
      const cachedFormData = loadFormCache()
      if (cachedFormData) {
        setLastFormData(cachedFormData)
      }
    }
  }, [lastFormData, loadFormCache])

  // 处理表单提交
  const handleFormSubmit = async (formData: CopywritingFormCache) => {
    setLastFormData(formData)
    saveFormCache(formData)

    // 直接从所有文案风格中查找（不再依赖 projectStyleId 分组）
    const selectedStyles = formData.copywritingStyleIds
      .map((id: string) => copywritingStyleById[id])
      .filter(Boolean)  // 过滤掉 undefined

    console.log('[handleFormSubmit] 调试信息:', {
      copywritingStyleIds: formData.copywritingStyleIds,
      allCopywritingStylesCount: copywritingStyles.length,
      selectedStylesCount: selectedStyles.length,
      selectedStyles,
    })

    // 启动生成（不等待完成）
    generateCopywriting({
      referenceText: formData.referenceText,
      referenceAnalysis: referenceAnalysis ? JSON.stringify(referenceAnalysis) : undefined,
      receiptType: 'ugc' as ReceiptType,  // 暂时使用默认值
      styleIds: formData.copywritingStyleIds,
      styles: selectedStyles,  // 传递完整的风格配置
      background: formData.background,
      requirements: formData.requirements,
      count: formData.count,
      assets: assets,
    })
    
    // 立即切换到结果页
    setStep('results')
  }

  // 保存选中的笔记
  const handleSaveSelected = async () => {
    if (!lastFormData) return []

    const meta: Omit<GenerationMeta, 'generated_at'> = {
      reference_text: lastFormData.referenceText,
      reference_analysis: referenceAnalysis ? JSON.stringify(referenceAnalysis) : undefined,
      copywriting_style_ids: lastFormData.copywritingStyleIds,
      background: lastFormData.background,
      requirements: lastFormData.requirements,
    }

    return saveSelectedNotes(meta)
  }

  // 继续创作（返回输入页）
  const handleContinueCreating = () => {
    clearResults()
    setStep('input')
  }

  // 返回输入页（保留结果）
  const handleBackToInput = () => {
    setStep('input')
  }

  return (
    <div className="space-y-6">
      {/* 步骤指示器 */}
      <div className="flex items-center gap-4">
        {step === 'results' && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToInput}
            className="-ml-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex items-center gap-2">
          <StepIndicator
            number={1}
            label="输入参数"
            active={step === 'input'}
            completed={step === 'results'}
          />
          <div className="w-8 h-px bg-border" />
          <StepIndicator
            number={2}
            label="编辑保存"
            active={step === 'results'}
            completed={false}
          />
        </div>
      </div>

      {/* 内容区域 */}
      {step === 'input' ? (
        <CopywritingInputForm
          project={project}
          isGenerating={isGenerating}
          isAnalyzing={isAnalyzing}
          error={error}
          referenceAnalysis={referenceAnalysis}
          onAnalyzeReference={analyzeReference}
          onSubmit={handleFormSubmit}
          loadFormCache={loadFormCache}
          saveFormCache={saveFormCache}
          // 如果有结果，显示「查看结果」按钮
          hasResults={generatedNotes.length > 0}
          onViewResults={() => setStep('results')}
        />
      ) : (
        <CopywritingResults
          notes={generatedNotes}
          isGenerating={isGenerating}
          generationProgress={generationProgress}
          error={error}
          onUpdateNote={updateGeneratedNote}
          onToggleSelection={toggleNoteSelection}
          onToggleSelectAll={toggleSelectAll}
          onSaveSelected={handleSaveSelected}
          onContinueCreating={handleContinueCreating}
          onNavigateToNotes={onNavigateToNotes}
        />
      )}
    </div>
  )
}

// 步骤指示器组件
function StepIndicator({
  number,
  label,
  active,
  completed,
}: {
  number: number
  label: string
  active: boolean
  completed: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`
          w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium
          transition-colors
          ${active
            ? 'bg-primary text-primary-foreground'
            : completed
              ? 'bg-primary/20 text-primary'
              : 'bg-muted text-muted-foreground'
          }
        `}
      >
        {completed ? '✓' : number}
      </div>
      <span
        className={`text-sm ${active ? 'font-medium' : 'text-muted-foreground'}`}
      >
        {label}
      </span>
    </div>
  )
}

